// noinspection JSUnusedLocalSymbols

import {
	EventDispatcher,
	Matrix4,
	Plane,
	Raycaster,
	Vector2,
	Vector3
} from 'three';

const _plane = new Plane();
const _raycaster = new Raycaster();

const _pointer = new Vector2();
const _offset = new Vector3();
const _intersection = new Vector3();
const _worldPosition = new Vector3();
const _inverseMatrix = new Matrix4();

class SnapDragControls extends EventDispatcher {
	/** @member {number} #downTimer Gestore del timer per differire l'evento di avvio di trascinamento. */
	#downTimer = 0;

	/**
	 *
	 * @param _objects
	 * @param _camera
	 * @param _domElement
	 * @param _step
	 * @param _limit_w
	 * @param _limit_h
	 * @param {Function} [_validateObject] Funzione che riceve l'oggetto intercettato dal raggio di selezione e restituisce il reale oggetto corrispondente, o null in caso non esista.
	 * @param {boolean} [_debug]
	 */
	constructor( _objects, _camera, _domElement, _step, _limit_w, _limit_h, _validateObject, _debug = false) {
		super();

		_domElement.style.touchAction = 'none'; // disable touch scroll

		let _selected = null, _hovered = null;
		let _delayed_selected = null;
		const _intersections = [];

		//
		const scope = this;
		this.#downTimer = 0;

		function activate() {
			_domElement.addEventListener( 'pointermove', onPointerMove );
			_domElement.addEventListener( 'pointerdown', onPointerDownDelay );
			_domElement.addEventListener( 'pointerup', onPointerCancel );
			_domElement.addEventListener( 'pointerleave', onPointerCancel );
		}

		function deactivate() {
			_domElement.removeEventListener( 'pointermove', onPointerMove );
			_domElement.removeEventListener( 'pointerdown', onPointerDownDelay );
			_domElement.removeEventListener( 'pointerup', onPointerCancel );
			_domElement.removeEventListener( 'pointerleave', onPointerCancel );

			_domElement.style.cursor = '';
		}

		function dispose() {
			deactivate();
		}

		function getObjects() {
			return _objects.children;
		}

		/**
		 * @returns {Raycaster}
		 */
		function getRaycaster() {
			return _raycaster;
		}

		function onPointerMove( event ) {
			if ( scope.enabled === false ) return;

			updatePointer( event );
			_raycaster.setFromCamera( _pointer, _camera );

			if ( _selected ) {
				if ( _raycaster.ray.intersectPlane( _plane, _intersection ) ) {
					const orig_y = _selected.position.y;
					const new_position = _selected.position.clone();
					new_position.copy( _intersection.sub( _offset ).applyMatrix4( _inverseMatrix ) );
					new_position.divideScalar(_step).floor().multiplyScalar(_step);//.addScalar(_step/2);
					// Vincola la posizione ai limiti
					const semi_width  = _limit_w / 2 - _step / 2;
					const semi_height = _limit_h / 2 - _step / 2;
					if (new_position.x < -semi_width)  {
						new_position.x = -semi_width;
					} else if (new_position.x > semi_width) {
						new_position.x =  semi_width;
					}
					if (new_position.z < -semi_height) {
						new_position.z = -semi_height;
					} else if (new_position.z > semi_height) {
						new_position.z = semi_height;
					}
					new_position.y = orig_y; // y position of dragged object (keep it on plane)

					if (!new_position.equals(_selected.position)) {
						// Aggiorna la posizione solo se effettivamente cambiata.
						_selected.position.copy(new_position);
						scope.dispatchEvent({type: 'drag', object: _selected});
					}
				}

				return;
			}

			// hover support
			if ( event.pointerType === 'mouse' || event.pointerType === 'pen' ) {
				_intersections.length = 0;
				_raycaster.intersectObjects( scope.getObjects(), true, _intersections );

				if ( _intersections.length > 0 ) {
					let object = _intersections[ 0 ].object;
					if (_validateObject) {
						object = _validateObject(object, _debug);
					}
					if ( _hovered !== object && _hovered !== null ) {
						scope.dispatchEvent( { type: 'hoveroff', object: _hovered } );
						_domElement.style.cursor = 'auto';
						_hovered = null;
					}
					if (!object) {
						return;
					}
					_plane.setFromNormalAndCoplanarPoint( _camera.getWorldDirection( _plane.normal ), _worldPosition.setFromMatrixPosition( object.matrixWorld ) );
					if ( _hovered !== object ) {
						scope.dispatchEvent( { type: 'hoveron', object: object } );
						_domElement.style.cursor = 'pointer';
						_hovered = object;
					}
				} else {
					if ( _hovered !== null ) {
						scope.dispatchEvent( { type: 'hoveroff', object: _hovered } );
						_domElement.style.cursor = 'auto';
						_hovered = null;
					}
				}
			}
		}

		/**
		 * Interrompe un eventuale evento rimandato.
		 * @returns {boolean} True se esisteva un evento che è stato interrotto.
		 */
		function clearPending() {
			if (scope.#downTimer) {
				_debug && console.log('Resetto ritardo per il drag.');
				window.clearTimeout(scope.#downTimer);
				scope.#downTimer = 0;
				return true;
			} else {
				return false;
			}
		}

		function onPointerDownDelay(event) {
			if ( scope.enabled === false ) return;
			clearPending();

			updatePointer( event );

			_intersections.length = 0;

			_raycaster.setFromCamera( _pointer, _camera );
			const o = scope.getObjects();
			_raycaster.intersectObjects( o, true, _intersections );
			let object = ( scope.transformGroup === true ) ? o[ 0 ] : (_intersections.length > 0 ? _intersections[ 0 ].object : null);
			if (_validateObject) {
				object = _validateObject(object, _debug);
			}
			// Ritarda il drag dell'oggetto per gestire il doppio click.
			if ( object ) {
				// _debug && console.log('Postpone drag %o', object, event);
				scope.#downTimer = window.setTimeout(onPointerDown, 100, [event]);
				_delayed_selected = object;
			} else {
				_debug && console.log('No room to drag.');
				_delayed_selected = null;
			}
		}

		function onPointerDown( event ) {
			scope.#downTimer = 0;
			// console.log('start drag %o', event, _intersections, _intersections.length);
			// _debug && console.log('Pointer down', scope.enabled);

			if ( scope.enabled === false ) {
				// _debug && console.log('failed drag: disabled');
				return
			}

			/*
			updatePointer( event );
			_intersections.length = 0;

			_raycaster.setFromCamera( _pointer, _camera );
			_raycaster.intersectObjects( _objects, true, _intersections );
			 */

			if ( _delayed_selected ) {
				_selected = _delayed_selected;

				_plane.setFromNormalAndCoplanarPoint( _camera.getWorldDirection( _plane.normal ), _worldPosition.setFromMatrixPosition( _selected.matrixWorld ) );

				if ( _raycaster.ray.intersectPlane( _plane, _intersection ) ) {
					_inverseMatrix.copy( _selected.parent.matrixWorld ).invert();
					_offset.copy( _intersection ).sub( _worldPosition.setFromMatrixPosition( _selected.matrixWorld ) );
				}

				_domElement.style.cursor = 'move';
				scope.dispatchEvent( { type: 'dragstart', object: _selected } );
			} else {
				// _debug && console.log('failed drag: no geometry found');
			}
		}

		function onPointerCancel() {
			// _debug && console.info('drag_cancel');
			if ( scope.enabled === false ) return;

			clearPending();

			if ( _selected ) {
				scope.dispatchEvent( { type: 'dragend', object: _selected } );
				_selected = null;
			}

			_domElement.style.cursor = _hovered ? 'pointer' : 'auto';
		}

		function updatePointer( event ) {
			// Calculate pointer position in normalized device coordinates
			const rect = _domElement.getBoundingClientRect();

			_pointer.x = ( event.clientX - rect.left ) / rect.width * 2 - 1;
			_pointer.y = - ( event.clientY - rect.top ) / rect.height * 2 + 1;
		}

		activate();

		// API

		this.enabled = true;
		this.transformGroup = false;

		this.activate = activate;
		this.deactivate = deactivate;
		this.dispose = dispose;
		this.getObjects = getObjects;
		this.getRaycaster = getRaycaster;
		this.clearPending = clearPending;
	}

	/**
	 * Restituisce se l'inizio del drag è stato ritardato ed è in attesa di esecuzione.
	 * @returns {boolean}
	 */
	get isPending() {
		return this.#downTimer !== 0;
	}

}

export { SnapDragControls };
